import { getSeriesInfoFromUPlot } from 'features/timeSeries/components/rulerTool/useRulerTool'
import {
  chartPenId,
  type ComputedTrendMarker,
} from 'features/timeSeries/computed/computedTrendStore'
import React, { useEffect, useRef } from 'react'
import type uPlot from 'uplot'

import {
  findNearestIndexByTime,
  type PointAnnotation,
} from './usePointAnnotationTool'

export const SEEDED_MARKER_ANNOTATION_ID = 'point-annotation-chat-marker'

export function markerSensorId(entryId: string): string {
  return chartPenId(entryId)
}

/** Identity of one seeding; a re-clicked citation gets a new key. */
export function seededMarkerKey(marker: ComputedTrendMarker): string {
  return [
    marker.entryId,
    marker.timestamp,
    marker.value,
    marker.requestedAt ?? '',
  ].join('|')
}

/**
 * Fallback y position while no pen is resolvable; once the annotation has its
 * sensorId the overlay snaps to the pen's nearest sample instead.
 */
export function markerAnchorRatio(
  scaleMin: number | null | undefined,
  scaleMax: number | null | undefined,
  value: number
): number {
  if (
    typeof scaleMin !== 'number' ||
    typeof scaleMax !== 'number' ||
    scaleMax <= scaleMin
  ) {
    return 0.5
  }
  return Math.min(1, Math.max(0, (scaleMax - value) / (scaleMax - scaleMin)))
}

/**
 * Seed one point annotation for a chat-cited value. Seeds once per click so
 * deleting it is not undone until the citation is clicked again.
 */
export function useSeededMarkerAnnotation({
  uPlotRef,
  marker,
  dataRevision,
  setAnnotations,
}: {
  uPlotRef: React.RefObject<uPlot | null>
  marker: ComputedTrendMarker | null | undefined
  dataRevision: unknown
  setAnnotations: React.Dispatch<React.SetStateAction<PointAnnotation[]>>
}): void {
  const seededKeyRef = useRef<string | null>(null)
  const markerKey = marker ? seededMarkerKey(marker) : null

  useEffect(() => {
    if (!marker || !markerKey || seededKeyRef.current === markerKey) return

    const trySeed = (): boolean => {
      const u = uPlotRef.current
      const xValues = u?.data[0] as ArrayLike<number> | undefined
      if (!u || !xValues || xValues.length === 0) return false
      const sensorId = markerSensorId(marker.entryId)
      const series = getSeriesInfoFromUPlot(u).find(
        (item) => item.sensorId === sensorId && item.show && !item.isRegression
      )
      if (!series) return false
      const target = Date.parse(marker.timestamp)
      if (!Number.isFinite(target)) {
        seededKeyRef.current = markerKey
        return true
      }
      const nearestIndex = findNearestIndexByTime(xValues, target)
      const anchorTimestamp = Number(xValues[nearestIndex])
      if (!Number.isFinite(anchorTimestamp)) return false
      const scale = u.scales[series.scale]
      const annotation: PointAnnotation = {
        id: SEEDED_MARKER_ANNOTATION_ID,
        anchorTimestamp,
        anchorYRatio: markerAnchorRatio(scale?.min, scale?.max, marker.value),
        sensorId,
        displayValue: marker.value,
        displayTimestamp: target,
      }
      seededKeyRef.current = markerKey
      setAnnotations((current) => {
        const index = current.findIndex(
          (item) => item.id === SEEDED_MARKER_ANNOTATION_ID
        )
        if (index === -1) return [...current, annotation]
        const next = [...current]
        next[index] = annotation
        return next
      })
      return true
    }

    if (trySeed()) return
    const frame = requestAnimationFrame(() => {
      trySeed()
    })
    return () => cancelAnimationFrame(frame)
  }, [marker, markerKey, dataRevision, setAnnotations, uPlotRef])
}
